/**
 * Salva un allegato email nel documentale: picker cartella searchable
 * (filtro client-side sul percorso completo) + nome file editabile.
 *
 * Feedback operazioni: nessun toast globale montato nell'app (sonner e'
 * esportato da @floraltech/ui ma senza <Toaster/> nel root, quindi
 * toast.success() non apparirebbe a schermo - vedi EtichetteManager.tsx).
 * Si riusa lo stesso pattern inline: errore in rosso, esito positivo in
 * verde, poi chiusura automatica del dialog.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Combobox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@floraltech/ui';
import { emailFetch, type EmailAllegato } from '../lib/api';

interface Cartella {
  id: string;
  parent_id: string | null;
  nome: string;
  percorso_completo: string;
}

interface Props {
  messaggioId: string;
  allegato: EmailAllegato | null;
  onClose: () => void;
}

const CHIUSURA_SUCCESSO_MS = 1500;

export default function SalvaDocumentaleDialog({ messaggioId, allegato, onClose }: Props) {
  const [cartelle, setCartelle] = useState<Cartella[]>([]);
  const [filtro, setFiltro] = useState('');
  const [cartellaId, setCartellaId] = useState<string | null>(null);
  const [nomeFile, setNomeFile] = useState('');
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [messaggio, setMessaggio] = useState<string | null>(null);

  // Identifica la "sessione" corrente (incrementata a ogni apertura/chiusura
  // del dialog) per scartare risposte tardive di fetch avviate su un
  // allegato precedente. Il timeout di chiusura automatica viene tracciato
  // per poterlo cancellare se il dialog cambia allegato prima che scatti.
  const generazioneRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset dello stato del form a ogni apertura (nuovo allegato selezionato)
  // e caricamento cartelle disponibili dal documentale.
  useEffect(() => {
    generazioneRef.current += 1;
    const generazione = generazioneRef.current;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (!allegato) return;
    setNomeFile(allegato.filename ?? 'allegato');
    setCartellaId(null);
    setFiltro('');
    setInCorso(false);
    setErrore(null);
    setMessaggio(null);
    const token = localStorage.getItem('token');
    fetch('/api/v1/archivio/cartelle', {
      headers: { Authorization: `Bearer ${token ?? ''}` },
    })
      .then((r) => {
        if (!r.ok) {
          if (generazioneRef.current === generazione) setErrore('Errore nel caricamento delle cartelle');
          return [];
        }
        return r.json();
      })
      .then((data: Cartella[]) => {
        if (generazioneRef.current === generazione) setCartelle(data);
      })
      .catch(() => {
        if (generazioneRef.current === generazione) {
          setErrore('Errore nel caricamento delle cartelle');
          setCartelle([]);
        }
      });

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [allegato]);

  const options = useMemo(
    () =>
      cartelle
        .filter((c) => c.percorso_completo.toLowerCase().includes(filtro.toLowerCase()))
        .map((c) => ({ value: c.id, label: c.percorso_completo })),
    [cartelle, filtro]
  );

  const leggiErrore = async (res: Response) => {
    const body = await res.json().catch(() => null);
    return typeof body?.detail === 'string' ? body.detail : `Errore ${res.status}`;
  };

  const salva = async () => {
    if (!allegato || !cartellaId) return;
    const generazione = generazioneRef.current;
    setInCorso(true);
    setErrore(null);
    setMessaggio(null);
    try {
      const res = await emailFetch(
        `/messaggi/${messaggioId}/allegati/${allegato.id}/salva-documentale`,
        {
          method: 'POST',
          body: JSON.stringify({ cartella_id: cartellaId, nome_file: nomeFile || null }),
        }
      );
      // Il dialog e' stato chiuso/riaperto su un altro allegato nel frattempo:
      // ignora questa risposta tardiva, non e' piu' la sessione corrente.
      if (generazioneRef.current !== generazione) return;
      if (!res.ok) {
        setErrore(await leggiErrore(res));
        return;
      }
      const percorso = cartelle.find((c) => c.id === cartellaId)?.percorso_completo ?? '';
      setMessaggio(`Salvato in ${percorso}`);
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        if (generazioneRef.current === generazione) onClose();
      }, CHIUSURA_SUCCESSO_MS);
    } catch {
      if (generazioneRef.current === generazione) setErrore('Errore di rete');
    } finally {
      if (generazioneRef.current === generazione) setInCorso(false);
    }
  };

  return (
    <Dialog open={!!allegato} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Salva nel Documentale</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="doc-cartella">Cartella di destinazione</Label>
            <Combobox
              options={options}
              value={filtro}
              onInputChange={(v) => {
                setFiltro(v);
                setCartellaId(null);
              }}
              onSelect={(opt) => {
                setCartellaId(opt.value);
                setFiltro(opt.label);
              }}
              placeholder="Cerca cartella..."
              autoFocus
              disabled={inCorso}
            />
          </div>
          <div>
            <Label htmlFor="doc-nomefile">Nome file</Label>
            <Input
              id="doc-nomefile"
              value={nomeFile}
              onChange={(e) => setNomeFile(e.target.value)}
              disabled={inCorso}
            />
          </div>
          {errore && <p className="text-sm text-destructive">{errore}</p>}
          {messaggio && (
            <p className="text-sm text-green-700 dark:text-green-400">{messaggio}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={inCorso}>
            Annulla
          </Button>
          <Button onClick={salva} disabled={!cartellaId || inCorso || !!messaggio}>
            {inCorso ? 'Salvo...' : 'Salva'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
